import { z } from "zod";

const optionalTimestampSchema = z
  .string()
  .nullish()
  .transform((value) => (value ? new Date(value).getTime() : undefined));

export const reviewCostSummarySchema = z.object({
  totalCostUsd: z.number().default(0),
  totalInputTokens: z.number().default(0),
  totalOutputTokens: z.number().default(0),
  totalTurns: z.number().default(0),
  runCount: z.number().default(0),
  lastModel: z.string().nullish(),
  lastHeadSha: z.string().nullish(),
});
export type ReviewCostSummary = z.infer<typeof reviewCostSummarySchema>;

export const reviewRunCostSchema = z.object({
  totalCostUsd: z.number().default(0),
  inputTokens: z.number().default(0),
  outputTokens: z.number().default(0),
  numTurns: z.number().default(0),
  runCount: z.number().default(0),
});
export type ReviewRunCost = z.infer<typeof reviewRunCostSchema>;

export const reviewRunOutcomeSchema = z.object({
  action: z.string().nullish(),
  verdict: z.string().nullish(),
  reason: z.string().default(""),
  skipReason: z.string().nullish(),
  confidence: z.string().nullish(),
  summary: z.string().default(""),
  inlineCount: z.number().default(0),
  droppedOutOfDiff: z.number().default(0),
  posted: z.boolean().default(false),
  error: z.string().nullish(),
});
export type ReviewRunOutcome = z.infer<typeof reviewRunOutcomeSchema>;

export const reviewRunSummarySchema = z.object({
  runId: z.string(),
  repo: z.string().default(""),
  pr: z.number().default(0),
  headSha: z.string().nullish(),
  title: z.string().default(""),
  kind: z.string().default(""),
  trigger: z.string().default(""),
  status: z.string().default(""),
  model: z.string().nullish(),
  textModel: z.string().nullish(),
  cost: reviewRunCostSchema,
  outcome: reviewRunOutcomeSchema,
  strict: z.boolean().default(false),
  filesChanged: z.array(z.string()).default([]),
  artifacts: z.array(z.string()).default([]),
  createdAt: optionalTimestampSchema,
  updatedAt: optionalTimestampSchema,
});
export type ReviewRunSummary = z.infer<typeof reviewRunSummarySchema>;

export const capCodeReviewPRSummarySchema = z.object({
  docId: z.string(),
  repo: z.string().default(""),
  pr: z.number().default(0),
  sha: z.string().default(""),
  closed: z.boolean().default(false),
  lastReviewTs: z.string().default(""),
  cost: reviewCostSummarySchema,
  createdAt: optionalTimestampSchema,
  updatedAt: optionalTimestampSchema,
});
export type CapCodeReviewPRSummary = z.infer<typeof capCodeReviewPRSummarySchema>;

export const capCodeReviewPRDetailSchema = capCodeReviewPRSummarySchema.extend({
  noVerdictSha: z.string().default(""),
  noVerdictAt: z.string().default(""),
  runCount: z.number().default(0),
  runs: z.array(reviewRunSummarySchema).default([]),
});
export type CapCodeReviewPRDetail = z.infer<typeof capCodeReviewPRDetailSchema>;

export const reviewRunArtifactSchema = z.object({
  kind: z.string(),
  content: z.string().default(""),
  totalChars: z.number().default(0),
  totalChunks: z.number().default(0),
});
export type ReviewRunArtifact = z.infer<typeof reviewRunArtifactSchema>;

export const existingReviewFeedbackSchema = z.object({
  feedback: z.string(),
  updatedAt: optionalTimestampSchema,
});

export const reviewFeedbackTargetSchema = z.object({
  feedbackId: z.string(),
  repo: z.string(),
  pr: z.number(),
  reviewedCommit: z.string(),
  runId: z.string(),
  model: z.string().nullish(),
  promptSha256: z.string(),
  githubCommentId: z.number(),
  githubCommentUrl: z.string().url(),
  findingBody: z.string(),
  findingTitle: z.string().default(""),
  path: z.string(),
  line: z.number(),
  startLine: z.number().nullish(),
  side: z.string(),
  severity: z.string(),
  existingFeedback: existingReviewFeedbackSchema.nullish(),
  createdAt: optionalTimestampSchema,
});
export type ReviewFeedbackTarget = z.infer<typeof reviewFeedbackTargetSchema>;

export const reviewFeedbackSubmissionSchema = z.object({
  feedbackId: z.string(),
  feedback: z.string(),
  submittedAt: optionalTimestampSchema,
});

export const pageInfoSchema = z.object({
  hasNextPage: z.boolean(),
  endCursor: z.string().nullish(),
});

export const reviewFeedbackListItemSchema = z.object({
  feedbackId: z.string(),
  repo: z.string(),
  pr: z.number(),
  githubCommentUrl: z.string().url(),
  findingBody: z.string(),
  findingTitle: z.string().default(""),
  path: z.string(),
  line: z.number(),
  severity: z.string(),
  feedback: z.string(),
  authorUserId: z.string(),
  submittedAt: optionalTimestampSchema,
  updatedAt: optionalTimestampSchema,
});
export type ReviewFeedbackListItem = z.infer<typeof reviewFeedbackListItemSchema>;

export const listReviewFeedbackResponseSchema = z.object({
  items: z.array(reviewFeedbackListItemSchema),
  pageInfo: pageInfoSchema,
});
export type ListReviewFeedbackResponse = z.infer<typeof listReviewFeedbackResponseSchema>;

export const listCapCodeReviewPRsResponseSchema = z.object({
  prs: z.array(capCodeReviewPRSummarySchema),
  pageInfo: pageInfoSchema,
});
export type ListCapCodeReviewPRsResponse = z.infer<typeof listCapCodeReviewPRsResponseSchema>;

export const dailyCountPointSchema = z.object({
  date: z.string(),
  count: z.number().default(0),
});
export type DailyCountPoint = z.infer<typeof dailyCountPointSchema>;

export const dailyCostPointSchema = z.object({
  date: z.string(),
  totalCostUsd: z.number().default(0),
});
export type DailyCostPoint = z.infer<typeof dailyCostPointSchema>;

export const dailyAverageCostPointSchema = z.object({
  date: z.string(),
  averageCostUsd: z.number(),
});
export type DailyAverageCostPoint = z.infer<typeof dailyAverageCostPointSchema>;

export const dailyAverageDurationPointSchema = z.object({
  date: z.string(),
  averageDurationSeconds: z.number(),
});
export type DailyAverageDurationPoint = z.infer<typeof dailyAverageDurationPointSchema>;

export const capCodeReviewMetricsSchema = z.object({
  days: z.number().default(7),
  costPerDay: z.array(dailyCostPointSchema).default([]),
  costPerPrPerDay: z.array(dailyAverageCostPointSchema).default([]),
  costPerRunPerDay: z.array(dailyAverageCostPointSchema).default([]),
  durationPerReviewPerDay: z.array(dailyAverageDurationPointSchema).default([]),
  runsPerDay: z.array(dailyCountPointSchema).default([]),
});
export type CapCodeReviewMetrics = z.infer<typeof capCodeReviewMetricsSchema>;
